import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { login } from '../../api/auth';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { Tabs, message } from 'antd';
import FeishuQRCode from '../../components/FeishuQRCode';
import JigsawCaptcha from '../../components/JigsawCaptcha';
import MD5 from 'crypto-js/md5';
import './index.css';
// @ts-ignore - Vite static asset
import loginHeroImg from '../../assets/images/login-hero-ai-refined-v4.png';
import {
  buildFeishuAuthorizeUrl,
  getFeishuAppId,
  getFeishuRedirectUri,
  isInFeishuClient,
} from '../../utils/feishu';

// 登录状态枚举
enum LoginStateEnum {
  ACCOUNT = 'account',
  RESET_PASSWORD = 'reset_password',
  QR_CODE = 'qr_code',
}

const Login = () => {
  const navigate = useNavigate();
  const { login: setAuth } = useAuth();
  
  const [searchParams, setSearchParams] = useSearchParams();

  // 登录方式：'account' 账户密码登录, 'feishu' 飞书登录（支持 ?tab=feishu / ?tab=account 深链）
  const [loginType, setLoginType] = useState(() => {
    if (typeof window === 'undefined') return 'account';
    const t = new URLSearchParams(window.location.search).get('tab');
    return t === 'feishu' || t === 'account' ? t : 'account';
  });
  const [loginState, setLoginState] = useState(LoginStateEnum.ACCOUNT);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [agreed, setAgreed] = useState(true);
  const [formData, setFormData] = useState({
    Username: '',
    Password: '',
    request_id: '',
    datas: [] as any[],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifyVisible, setVerifyVisible] = useState(false);
  const [fsShow, setFsShow] = useState(false);
  const [client_id, setClient_id] = useState('');
  const [logoutCallback, setLogoutCallback] = useState('');


  /**
   * 飞书内免登（自动跳转）
   *
   * 说明：
   * - AuthGuard 在飞书客户端内触发登录时，会写入 sessionStorage: AUTO_FEISHU_LOGIN=1
   * - 本页在拿到 ReturnUrl 后，直接跳转飞书 OAuth（不需要扫码）
   * - 回调仍走 `public/fsCallback.html -> /auth/account/thirdlogin -> OIDC -> /callback`
   */
  useEffect(() => {
    // 先把 ReturnUrl 缓存下来（fsCallback.html 会从 localStorage 读取）
    const returnUrlFromQuery = searchParams.get('ReturnUrl');
    if (returnUrlFromQuery) {
      try {
        localStorage.setItem('ReturnUrl', decodeURIComponent(returnUrlFromQuery));
      } catch (e) {
        localStorage.setItem('ReturnUrl', returnUrlFromQuery);
      }
    }
    const shouldAutoFeishuLogin = isInFeishuClient();
    if (!shouldAutoFeishuLogin) return;

    // 避免循环跳转（比如授权失败/取消）
    if (sessionStorage.getItem('AUTO_FEISHU_LOGIN_TRIED') === '1') return;

    // thirdlogin 继续完成 OIDC 授权需要 ReturnUrl
    const storedReturnUrl = localStorage.getItem('ReturnUrl') || '';
    if (!storedReturnUrl) return;

    const appId = getFeishuAppId();
    if (!appId) return;

    sessionStorage.setItem('AUTO_FEISHU_LOGIN_TRIED', '1');
    setLoginType('feishu');

    window.location.href = buildFeishuAuthorizeUrl();
  }, [searchParams]);

  // 地址栏 tab 与登录方式同步（浏览器前进/后退、仅改 query 时）
  useEffect(() => {
    if (isInFeishuClient()) return;
    const t = searchParams.get('tab');
    if (t === 'feishu' || t === 'account') {
      setLoginType(t);
    } else {
      setLoginType('account');
    }
  }, [searchParams]);

  const handleLoginTabChange = (key: string) => {
    setLoginType(key);
    setError('');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === 'account') {
          next.delete('tab');
        } else {
          next.set('tab', key);
        }
        return next;
      },
      { replace: true },
    );
  };

  // 飞书登录成功回调
  const handleFeishuSuccess = (tmpCode: string) => {
    // 如果需要自定义处理，可以在这里实现
  };

  // 飞书登录错误回调
  const handleFeishuError = (error: Error) => {
    setError(error.message || '飞书登录失败');
    console.error('飞书登录错误:', error);
  };

  // 表单验证
  const validForm = () => {
    if (!formData.Username) {
      setError('请输入用户名');
      return null;
    }
    if (!formData.Password) {
      setError('请输入密码');
      return null;
    }
    if (!agreed) {
      setError('请阅读并勾选页面底部的服务协议');
      return null;
    }
    return formData;
  };

  // 清除验证参数
  const clearVerifyParams = () => {
    setFormData((prev) => ({
      ...prev,
      request_id: '',
      datas: [],
    }));
    setVerifyVisible(false);
  };

  // 重置表单
  const reset = () => {
    setFormData({
      Username: '',
      Password: '',
      request_id: '',
      datas: [],
    });
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // 清除错误信息
    if (error) setError('');
  };

  // 登录处理函数
  // traceData: 人机校验通过后返回的拖动轨迹数据（数组）
  const handleLogin = async (traceData?: number[]) => {
    const data = validForm();
    if (!data) return;
    
    setLoading(true);
    
    try {
      // 获取三方登录 Code
      const thirdCodeStr = sessionStorage.getItem('ThirdCode');
      const code = thirdCodeStr ? JSON.parse(thirdCodeStr) : null;
      
      // 获取 ReturnUrl（优先使用 query 参数，否则使用 localStorage）
      const returnUrl = searchParams.get('ReturnUrl') || localStorage.getItem('ReturnUrl') || '';
      if (!returnUrl) {
        // 如果没有 ReturnUrl，使用默认路径或当前路径
        console.warn('未找到 ReturnUrl 参数，使用默认路径');
      }
      
      // 解析 ReturnUrl 中的参数
      const decodedUrl = returnUrl ? decodeURIComponent(returnUrl) : '';
      localStorage.setItem('ReturnUrl', decodedUrl);
      const urlParts = decodedUrl.split('?');
      let clientId = '';
      let redirectUri = '';
      
      if (urlParts.length > 1) {
        const queryString = urlParts[1];
        const params = queryString.split('&');
        
        params.forEach((item) => {
          if (item.includes('client_id')) {
            clientId = item.split('=')[1];
          }
          if (item.includes('redirect_uri')) {
            const redirectUriPart = item.split('=')[1];
            redirectUri = redirectUriPart.split('callback')[0] + 'login';
            localStorage.setItem('logoutCallback', redirectUri);
            setLogoutCallback(redirectUri);
          }
        });
      }
      
      // 存储 client_id
      if (clientId) {
        sessionStorage.setItem('client_id', clientId);
        setClient_id(clientId);
      }
      
      // 使用 ReturnUrl（已在上方获取）
      const finalReturnUrl = returnUrl || '';
      
      // 构建登录参数
      const params = {
        Username: formData.Username,
        Password: MD5(formData.Password).toString(),
        RememberLogin: rememberMe,
        ReturnUrl: finalReturnUrl,
        RequestId: formData.request_id || '',
        // 如果有从人机校验返回的轨迹数据，优先使用；否则使用表单中已有的数据
        RequestData: traceData && traceData.length ? traceData : (formData.datas || []),
        ClientId: clientId || '',
      };
      
      // 判断是否需要三方绑定
      const isThirdLoginBind = fsShow && code;
      let loginParams: any = params;
      
      if (isThirdLoginBind) {
        // 三方绑定参数
        loginParams = {
          PlantCode: 'feishu',
          RedirectUri: `${window.location.origin}/fsCallback`,
          Code: code,
          ...params,
        };
      }
      
      // 调用登录接口
      const response: any = await login(loginParams);
      // 根据响应拦截器，response 已经是 response.data
      const userInfo = response?.res_content || response;
      if (userInfo) {
        setLoading(false);
        clearVerifyParams();
        
        // 处理首次登录（需要更改密码）
        if (userInfo.isFirstLogin) {
          message.error(userInfo.innerResponseMessage || '首次登录，请更改密码');
          reset();
          setLoginState(LoginStateEnum.RESET_PASSWORD);
          return;
        }
        
        // 处理人机校验
        if (userInfo.innerResponseCode === '50050' || userInfo.innerResponseCode === '50080') {
          setFormData((prev) => ({
            ...prev,
            request_id: userInfo.requestId || '',
          }));
          setVerifyVisible(true);
          // TODO: 初始化验证码 canvas
          // canvasInit();
          return;
        }
        
        // 处理二维码登录
        if (userInfo.innerResponseCode === '50062') {
          setLoginState(LoginStateEnum.QR_CODE);
          if (sessionStorage.getItem('ThirdCode')) {
            sessionStorage.removeItem('ThirdCode');
          }
          setFsShow(false);
          return;
        }
        
        // 处理其他错误
        if (userInfo.innerResponseCode !== '0' && userInfo.innerResponseCode !== '50050') {
          setLoading(false);
          setVerifyVisible(false);
          message.error(userInfo.innerResponseMessage || '登录失败');
          return;
        }
        
        // 登录成功
        if (userInfo.innerResponseCode === '0') {
          if (userInfo.returnUrl) {
            const returnUrl = decodeURIComponent(userInfo.returnUrl);
            window.location.href = returnUrl;
          } else {
            // 登录成功，跳转到首页
            navigate('/');
          }
        }
      } else {
        setLoading(false);
      }
    } catch (err: any) {
      setLoading(false);
      setError(err.message || '登录失败，请稍后重试');
      message.error(err.message || '登录失败，请稍后重试');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    await handleLogin();
  };

  // 如果在飞书环境，不显示登录页面
  // if (isInFeishuClient()) {
  //   return <div>在飞书客户端内，不显示登录页面</div>;
  // }
  return (
    isInFeishuClient() ? <div></div> : <div  className="login-container">
      <div className="login-background">
        <div className="bg-blur-circle bg-blur-1"></div>
        <div className="bg-blur-circle bg-blur-2"></div>
        <div className="bg-gradient-overlay"></div>
      </div>

      <img
        src={loginHeroImg}
        alt=""
        className="login-hero-fullbleed"
        draggable={false}
        aria-hidden
      />
      <div className="login-hero-scrim" aria-hidden />

      <div className="login-content-shell">
        <div className="login-content-spacer" aria-hidden />
        <div className="login-content">
        <div className="login-box">
          {/* <div className="login-header">
            <h2 className="login-title">欢迎回来</h2>
            <p className="login-subtitle">请输入您的账号以继续访问系统</p>
          </div> */}

          <section className="login-tabs" aria-label="选择登录方式">
            <Tabs
              activeKey={loginType}
              onChange={handleLoginTabChange}
              centered
              items={[
                { key: 'account', label: '账号登录' },
                { key: 'feishu', label: '飞书扫码' },
              ]}
            />
          </section>

          <div className="login-body">
            {/* 账户密码登录 */}
            {loginType === 'account' && (
              <form onSubmit={handleSubmit} className="login-form">
                {error && <div className="error-message">{error}</div>}
                
                <div className="form-group">
                  <label className="form-label">用户名</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      name="Username"
                      value={formData.Username}
                      onChange={handleChange}
                      placeholder="请输入用户名"
                      required
                      autoComplete="username"
                      className="form-input"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">密码</label>
                  <div className="input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="Password"
                      value={formData.Password}
                      onChange={handleChange}
                      placeholder="请输入密码"
                      required
                      autoComplete="current-password"
                      className="form-input"
                    />
                    <span 
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    </span>
                  </div>
                </div>
                
                <button 
                  type="submit" 
                  className="login-button"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="loading-text">正在处理...</span>
                  ) : (
                    <>
                      登录
                    </>
                  )}
                </button>
              </form>
            )}

            {/* 飞书扫码 */}
            {loginType === 'feishu' && (
              <div className="feishu-login">
                {error && <div className="error-message">{error}</div>}
                <FeishuQRCode
                  callbackPath="callback"
                  containerId="feishu-qrcode"
                  onSuccess={handleFeishuSuccess}
                  onError={handleFeishuError}
                  autoInit={true}
                  style={{ width: '100%', maxWidth: 300 }}
                />
              </div>
            )}
          </div>

          <div className="login-box-footer">
            <span>安全登录 · 统一身份认证</span>
            <span className="login-box-footer-dot" aria-hidden>
              ·
            </span>
            <span>支持账号密码与飞书扫码</span>
          </div>

          {/* 注册提示（当前不开放注册） */}
        </div>
        </div>
      </div>

      {/* 底部版权信息 */}
      {/* <footer className="login-footer">
        <p className="footer-copyright">© 2025 ENTERPRISE INTELLIGENCE. ALL RIGHTS RESERVED.</p>
      </footer> */}

      {/* 人机验证码 */}
      <JigsawCaptcha
        visible={verifyVisible}
        onSuccess={async (traceData?: number[]) => {
          // 验证成功，继续登录流程
          setVerifyVisible(false);
          // 重新调用登录接口，此时应该已经通过了人机校验
          await handleLogin(traceData as number[] | undefined);
        }}
        onFail={() => {
          // 验证失败，可以在这里处理
          console.log('验证失败');
        }}
        onClose={() => {
          setVerifyVisible(false);
          setLoading(false);
        }}
      />
    </div>
  );
};

export default Login;

